// maths.portll.net — child profiles + switcher (the Grown-Ups' Corner roster).
//
// Switching the active child IS just switching profiles (ProfileContext) — the trust
// boundary is the parent account, re-proved by Firestore rules on every read, so this
// screen is pure UX. Identity model: the cloud child doc holds a NON-PII `username`;
// the child's real name is a device-only override (displayNameFor / setChildLocalName)
// that never syncs. Picking a PIN-protected child routes through the numeric PinPad.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useProfiles } from "../contexts/ProfileContext.jsx";
import { createChild, updateChild, yearLabel } from "../lib/tasks.js";
import { callDeleteChildProfile } from "../lib/firebase.js";
import {
  PageHeader,
  Card,
  Chip,
  Button,
  Field,
  EmptyState,
  Spinner,
  Note,
  injectStyles,
} from "../components/ui.jsx";
import AvatarPicker, { Avatar, AVATARS } from "../components/AvatarPicker.jsx";
import PinPad from "../components/PinPad.jsx";

const YEARS = Array.from({ length: 11 }, (_, y) => y); // 0..10

function yearOption(y) {
  return y === 0 ? "Foundation (F)" : `Year ${y}`;
}

// ── add / edit form (shared) ─────────────────────────────────────────────────
function ChildForm({ initial, submitLabel, onSubmit, onCancel }) {
  const [username, setUsername] = useState(initial.username || "");
  const [localName, setLocalName] = useState(initial.localName || "");
  const [avatarId, setAvatarId] = useState(initial.avatarId || AVATARS[0].id);
  const [yearLevel, setYearLevel] = useState(Number(initial.yearLevel) || 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!username.trim()) {
      setErr("Please pick a username.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await onSubmit({ username: username.trim(), localName, avatarId, yearLevel });
    } catch (e2) {
      setErr(e2.message || "Couldn't save. Please try again.");
      setBusy(false);
    }
  }

  return (
    <Card as="form" className="cp-form" onSubmit={submit}>
      <Field
        label="Username"
        htmlFor="cp-username"
        hint="A fun handle, not a real name — this is what's stored in the cloud."
      >
        <input
          id="cp-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          maxLength={30}
          autoComplete="off"
          placeholder="e.g. star-counter"
        />
      </Field>

      <Field
        label="Name shown on this device only (optional)"
        htmlFor="cp-localname"
        hint="Stays on this device and is never sent to the cloud. Leave blank to just use the username."
      >
        <input
          id="cp-localname"
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
          maxLength={40}
          autoComplete="off"
          placeholder="e.g. Ada"
        />
      </Field>

      <Field label="Avatar">
        <AvatarPicker value={avatarId} onChange={setAvatarId} />
      </Field>

      <Field
        label="Year level"
        htmlFor="cp-year"
        hint="Pick the year that feels right — you can change it anytime."
      >
        <select id="cp-year" value={yearLevel} onChange={(e) => setYearLevel(Number(e.target.value))}>
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {yearOption(y)}
            </option>
          ))}
        </select>
      </Field>

      {err && <Note tone="danger">{err}</Note>}

      <div className="cp-form__actions">
        <Button type="submit" disabled={busy}>
          {busy ? <Spinner size={18} /> : submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

// ── typed-confirm delete modal ───────────────────────────────────────────────
function DeleteModal({ child, displayName, onConfirm, onCancel }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const target = displayName || child.username || "";
  const matches = typed.trim().toLowerCase() === target.trim().toLowerCase();

  async function go() {
    setBusy(true);
    setErr("");
    try {
      await onConfirm();
    } catch (e) {
      setErr(e.message || "Couldn't remove this profile. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="cp-modal" role="dialog" aria-modal="true" aria-label={`Remove ${target}`}>
      <Card className="cp-modal__card">
        <h2 style={{ marginTop: 0 }}>Remove {target}?</h2>
        <Note tone="danger">
          This permanently deletes this child's profile and everything you've recorded together —
          completions, datasets and any photos. This can't be undone.
        </Note>
        <Field label={`Type “${target}” to confirm`} htmlFor="cp-del-confirm">
          <input
            id="cp-del-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            placeholder={target}
          />
        </Field>
        {err && <Note tone="danger">{err}</Note>}
        <div className="cp-form__actions">
          <Button variant="danger" onClick={go} disabled={!matches || busy}>
            {busy ? <Spinner size={18} /> : "Remove profile"}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function ChildProfiles() {
  const { user } = useAuth();
  const {
    profiles,
    loading,
    activeId,
    switchProfile,
    setActive,
    setChildLocalName,
    displayNameFor,
    localNames,
  } = useProfiles();
  const nav = useNavigate();

  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [pinChild, setPinChild] = useState(null); // child requiring a PIN to open
  const [delChild, setDelChild] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  async function openChild(child) {
    setError("");
    if (child.hasPin) {
      setPinChild(child);
      return;
    }
    try {
      setBusyId(child.id);
      const res = await switchProfile(child.id);
      if (res.ok) {
        setActive(child.id);
        nav(`/grown-ups/child/${child.id}`);
      }
    } catch (e) {
      setError(e.message || "Couldn't open that profile.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleCreate({ username, localName, avatarId, yearLevel }) {
    const id = await createChild(user.uid, { username, avatarId, yearLevel });
    if (localName && localName.trim()) setChildLocalName(id, localName);
    setAdding(false);
  }

  async function handleEdit(child, { username, localName, avatarId, yearLevel }) {
    await updateChild(user.uid, child.id, { username, avatarId, yearLevel });
    setChildLocalName(child.id, localName); // set OR clear (empty removes it)
    setEditId(null);
  }

  async function confirmDelete(child) {
    await callDeleteChildProfile({ childId: child.id });
    if (activeId === child.id) setActive(null);
    setDelChild(null);
  }

  return (
    <main className="container">
      <PageHeader
        title="Children"
        subtitle="Add each child once, then tap to switch. Progress here is “explored”, never scored."
        back="/grown-ups"
      >
        {!adding && (
          <Button variant="secondary" onClick={() => { setAdding(true); setEditId(null); }}>
            + Add a child
          </Button>
        )}
      </PageHeader>

      {error && <Note tone="danger">{error}</Note>}

      {adding && (
        <ChildForm
          initial={{ username: "", localName: "", avatarId: AVATARS[0].id, yearLevel: 0 }}
          submitLabel="Add child"
          onSubmit={handleCreate}
          onCancel={() => setAdding(false)}
        />
      )}

      {loading ? (
        <div style={{ display: "grid", placeItems: "center", padding: "2rem" }}>
          <Spinner size={28} />
        </div>
      ) : profiles.length === 0 && !adding ? (
        <EmptyState
          title="No children yet"
          hint="Add your first child to start collecting Home Missions together."
          icon="👋"
          action={<Button onClick={() => setAdding(true)}>Add a child</Button>}
        />
      ) : (
        <ul className="cp-list">
          {profiles.map((child) => {
            const name = displayNameFor(child);
            const isEditing = editId === child.id;
            return (
              <li key={child.id}>
                {isEditing ? (
                  <ChildForm
                    initial={{
                      username: child.username || "",
                      localName: localNames[child.id] || "",
                      avatarId: child.avatarId || AVATARS[0].id,
                      yearLevel: child.yearLevel || 0,
                    }}
                    submitLabel="Save changes"
                    onSubmit={(vals) => handleEdit(child, vals)}
                    onCancel={() => setEditId(null)}
                  />
                ) : (
                  <Card className="cp-row">
                    <Avatar id={child.avatarId} size={48} />
                    <div className="cp-row__body">
                      <div className="cp-row__name">
                        {name || "Unnamed"}
                        {child.hasPin && (
                          <Chip tone="neutral">
                            <span aria-hidden="true">🔒</span> PIN
                          </Chip>
                        )}
                        {activeId === child.id && <Chip tone="ok">Active</Chip>}
                      </div>
                      <div className="cp-row__meta">Year {yearLabel(child.yearLevel)}</div>
                    </div>
                    <div className="cp-row__actions">
                      <Button size="sm" onClick={() => openChild(child)} disabled={busyId === child.id}>
                        {busyId === child.id ? <Spinner size={16} /> : "Open"}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => { setEditId(child.id); setAdding(false); }}
                      >
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDelChild(child)}>
                        Delete
                      </Button>
                    </div>
                  </Card>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pinChild && (
        <div className="cp-modal" role="dialog" aria-modal="true" aria-label={`Enter PIN for ${displayNameFor(pinChild)}`}>
          <Card className="cp-modal__card">
            <PinPad
              title={`Unlock ${displayNameFor(pinChild) || "this profile"}`}
              subtitle="Enter this child's 4-digit PIN to switch in."
              verify={(pin) => switchProfile(pinChild.id, pin)}
              onSuccess={() => {
                const id = pinChild.id;
                setActive(id);
                setPinChild(null);
                nav(`/grown-ups/child/${id}`);
              }}
            />
            <div className="cp-form__actions" style={{ justifyContent: "center", marginTop: ".75rem" }}>
              <Button variant="ghost" onClick={() => setPinChild(null)}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {delChild && (
        <DeleteModal
          child={delChild}
          displayName={displayNameFor(delChild)}
          onConfirm={() => confirmDelete(delChild)}
          onCancel={() => setDelChild(null)}
        />
      )}
    </main>
  );
}

injectStyles(
  "maths-childprofiles-styles",
  `
.cp-form{margin:0 0 1.25rem;}
.cp-form__actions{display:flex;flex-wrap:wrap;gap:.6rem;margin-top:.5rem;}
.cp-list{list-style:none;margin:0;padding:0;display:grid;gap:.75rem;}
.cp-row{display:flex;align-items:center;gap:.9rem;}
.cp-row__body{flex:1 1 8rem;min-width:0;}
.cp-row__name{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;font-weight:700;color:var(--brand-ink);}
.cp-row__meta{color:var(--ink-soft);font-size:.9rem;margin-top:.15rem;}
.cp-row__actions{display:flex;gap:.4rem;flex-wrap:wrap;justify-content:flex-end;flex:0 0 auto;}
.cp-modal{position:fixed;inset:0;z-index:50;display:grid;place-items:center;padding:1rem;
  background:color-mix(in srgb,var(--ink) 45%,transparent);overflow:auto;}
.cp-modal__card{width:min(26rem,100%);max-width:100%;}
@media (max-width:34rem){
  .cp-row{flex-wrap:wrap;}
  .cp-row__actions{width:100%;justify-content:flex-start;}
}
`
);
